import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppMounts, type AppMount, type AppNavMode } from '@/lib/apps-mounts';

export interface ActiveMountResolution {
  mount: AppMount;
  /**
   * Final nav_mode after layering tenant override → manifest preference →
   * default 'append'. Contract v1.6 §2.
   */
  effectiveNavMode: AppNavMode;
}

/**
 * Resolves which mounted app (if any) is currently being viewed by matching
 * `location.pathname` against `path_prefix` of every active mount.
 *
 * Match rules:
 *   - `/myapp` or `/myapp/anything` matches a mount with prefix `myapp`.
 *   - `/myapp-other` does NOT match `myapp` (the segment must end at `/`).
 *   - Nested prefixes (`team/foo`) are matched whole.
 *
 * Returns `null` when the user is on a host page (homepage, /shop, etc.) or
 * when the mount list hasn't loaded yet.
 */
export function useActiveMount(): ActiveMountResolution | null {
  const { pathname } = useLocation();
  const { data: mounts } = useAppMounts();

  return useMemo(() => {
    if (!mounts || mounts.length === 0) return null;
    for (const m of mounts) {
      const prefix = `/${m.path_prefix}`;
      if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
        const effectiveNavMode: AppNavMode =
          m.nav_mode_override ?? m.app.manifest?.nav_mode ?? 'append';
        return { mount: m, effectiveNavMode };
      }
    }
    return null;
  }, [pathname, mounts]);
}
